import {
	Component,
	EventEmitter,
	Input,
	OnChanges,
	Output,
} from '@angular/core';

import {
	ContractSection,
	ContractSectionsDocument,
	parseContractSections,
	serializeContractSections,
} from './contract-sections';

@Component({
	selector: 'app-contract-section-editor',
	styleUrls: ['./contract-section-editor.component.scss'],
	templateUrl: './contract-section-editor.component.html',
})
export class ContractSectionEditorComponent implements OnChanges {
	@Input() editable: boolean = false;
	@Input() value: string = '';
	@Output() readonly valueChange = new EventEmitter<string>();

	document: ContractSectionsDocument = parseContractSections('');
	private lastEmittedValue: string | null = null;

	get documentTitle(): string | null {
		const firstSection = this.document.sections[0];

		return this.isDocumentHeading(firstSection) ? firstSection.title : null;
	}

	get sections(): ContractSection[] {
		return this.isDocumentHeading(this.document.sections[0])
			? this.document.sections.slice(1)
			: this.document.sections;
	}

	addSection(): void {
		const lineEnding = this.getLineEnding();
		const currentSections = this.document.sections.map(
			(section, index, sections) =>
				index === sections.length - 1
					? {
							...section,
							trailingWhitespace: this.ensureSectionSeparation(
								section.trailingWhitespace,
								lineEnding,
							),
						}
					: section,
		);
		const newSection: ContractSection = {
			id: `contract-section-${currentSections.length}`,
			content: '',
			headingNewline: lineEnding,
			headingStyle: 'markdown',
			leadingWhitespace: '',
			originalTitle: 'Nova seção',
			rawHeading: '## Nova seção',
			title: 'Nova seção',
			titlePrefix: '## ',
			titleSuffix: '',
			trailingWhitespace: lineEnding,
		};

		this.document = {
			...this.document,
			sections: [...currentSections, newSection],
		};
		this.emitValue();
	}

	ngOnChanges(): void {
		if (this.value === this.lastEmittedValue) {
			this.lastEmittedValue = null;

			return;
		}

		this.lastEmittedValue = null;
		this.document = parseContractSections(this.value);
	}

	removeSection(sectionId: string): void {
		if (this.sections.length <= 1) {
			return;
		}

		this.document = {
			...this.document,
			sections: this.document.sections.filter(
				(section) => section.id !== sectionId,
			),
		};
		this.emitValue();
	}

	trackSection(_: number, section: ContractSection): string {
		return section.id;
	}

	updateContent(sectionId: string, content: string): void {
		this.updateSection(sectionId, {
			content,
		});
	}

	updateTitle(sectionId: string, event: Event): void {
		this.updateSection(sectionId, {
			title: (event.target as HTMLInputElement).value,
		});
	}

	private emitValue(): void {
		const value = serializeContractSections(this.document);

		this.lastEmittedValue = value;
		this.valueChange.emit(value);
	}

	private ensureSectionSeparation(
		whitespace: string,
		lineEnding: string,
	): string {
		return `${whitespace.replace(/(?:\r\n|\n|\r)+$/u, '')}${lineEnding}${lineEnding}`;
	}

	private getLineEnding(): string {
		return this.value.includes('\r\n') ? '\r\n' : '\n';
	}

	private isDocumentHeading(section?: ContractSection): boolean {
		return Boolean(
			section &&
				this.document.sections.length > 1 &&
				section.headingStyle === 'markdown' &&
				section.titlePrefix.trim() === '#' &&
				!section.content.trim(),
		);
	}

	private updateSection(
		sectionId: string,
		changes: Partial<Pick<ContractSection, 'content' | 'title'>>,
	): void {
		this.document = {
			...this.document,
			sections: this.document.sections.map((section) =>
				section.id === sectionId ? { ...section, ...changes } : section,
			),
		};
		this.emitValue();
	}
}
